import React from 'react';
import { Download, Trash2, FileArchive, CheckCircle, XCircle } from 'lucide-react';

function formatFileSize(bytes) {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

export default function ConversionResults({
  results,
  showDownloadAll,
  showClearResults,
  onDownloadAll,
  onClear
}) {
  if (results.length === 0) return null;

  const successCount = results.filter(r => r.dataURL).length;
  const errorCount = results.filter(r => r.error).length;

  return (
    <div className="w-full bg-slate-800/50 backdrop-blur-md rounded-2xl border border-slate-700/50 p-6 animate-slide-up">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 to-cyan-400">
          Results
        </h2>
        <div className="flex items-center gap-3 text-sm">
          {successCount > 0 && (
            <span className="flex items-center gap-1 text-green-400">
              <CheckCircle size={14} /> {successCount} converted
            </span>
          )}
          {errorCount > 0 && (
            <span className="flex items-center gap-1 text-red-400">
              <XCircle size={14} /> {errorCount} failed
            </span>
          )}
        </div>
      </div>

      <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
        {results.map((item, index) => (
          <div
            key={item.newName || `error-${index}`}
            className={`flex items-center justify-between p-3 rounded-lg text-sm transition-colors ${
              item.error
                ? 'bg-red-500/10 border border-red-500/30'
                : 'bg-slate-700/50 hover:bg-slate-700/70'
            }`}
          >
            <div className="flex-1 min-w-0 mr-3">
              <p className="text-gray-300 truncate">
                {item.originalName}
                {item.newName && <span className="text-gray-500"> → {item.newName}</span>}
              </p>
              {item.error && <p className="text-red-400 text-xs mt-0.5">{item.error}</p>}
              {item.originalSize && item.outputSize && (
                <p className="text-xs text-gray-500 mt-0.5">
                  {formatFileSize(item.originalSize)} → {formatFileSize(item.outputSize)}
                  {' '}
                  <span className={item.outputSize < item.originalSize ? 'text-green-400' : 'text-yellow-400'}>
                    ({item.outputSize < item.originalSize ? '-' : '+'}
                    {Math.abs(Math.round((1 - item.outputSize / item.originalSize) * 100))}%)
                  </span>
                </p>
              )}
            </div>
            {item.dataURL && item.newName && (
              <a
                href={item.dataURL}
                download={item.newName}
                className="flex items-center gap-1 text-indigo-400 hover:text-indigo-300 font-medium flex-shrink-0 transition-colors"
                aria-label={`Download ${item.newName}`}
              >
                <Download size={14} />
                <span className="hidden sm:inline">Download</span>
              </a>
            )}
          </div>
        ))}
      </div>

      <div className="mt-4 flex flex-col sm:flex-row gap-2">
        {showDownloadAll && (
          <button
            onClick={onDownloadAll}
            className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white font-semibold py-2.5 px-4 rounded-xl transition-all duration-300 transform hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
          >
            <FileArchive size={16} />
            Download All as ZIP
          </button>
        )}
        {showClearResults && (
          <button
            onClick={() => {
              if (window.confirm('Clear all results? Unconverted files will be lost.')) {
                onClear();
              }
            }}
            className="flex-1 flex items-center justify-center gap-2 bg-red-500/20 hover:bg-red-500/30 text-red-300 font-semibold py-2.5 px-4 rounded-xl border border-red-500/30 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-red-500/50"
          >
            <Trash2 size={16} />
            Clear Results
          </button>
        )}
      </div>
    </div>
  );
}
